import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Users, BedDouble, PercentCircle, LogIn, AlertTriangle, GraduationCap, ChevronRight } from 'lucide-react';

interface SuperAdminHomeProps {
  clients: any[];
  rooms: any[];
  sites: any[];
  alerts: any[];
  totalCapacity: number;
  checkedInCount: number;
  dailyRosterCount?: number;
  userName?: string;
  onNavigate?: (section: string) => void;
}

/**
 * Home overview: census snapshot + action items.
 * Agency Analytics lives exclusively in the sidebar Analytics tab.
 */
const SuperAdminHome: React.FC<SuperAdminHomeProps> = ({
  clients,
  alerts,
  totalCapacity,
  checkedInCount,
  onNavigate,
}) => {
  const { isDemoMode } = useAuth();

  const activeClients = clients.filter((c: any) => !c.archived_at && c.status !== 'unbedded');
  const unbeddedCount = clients.filter((c: any) => !c.archived_at && c.status === 'unbedded').length;
  const occupied = clients.filter((c: any) => !c.archived_at && c.room_id).length;
  const bedsAvailable = Math.max(0, totalCapacity - occupied);
  const occupancy = totalCapacity > 0 ? Math.round((occupied / totalCapacity) * 100) : 0;
  const openAlerts = alerts.filter((a: any) => a.status === 'active').length;

  // Overdue trainings count (same source as OverdueTrainingsCard)
  const { data: overdueTrainings = 0 } = useQuery({
    queryKey: ['home-overdue-trainings-count'],
    enabled: !isDemoMode,
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const [assignmentsRes, completionsRes, rolesRes] = await Promise.all([
        supabase
          .from('training_assignments')
          .select('material_id, assigned_to_user_id, assigned_to_role, due_date')
          .not('due_date', 'is', null)
          .lt('due_date', today),
        supabase.from('training_completions').select('material_id, user_id'),
        supabase.from('user_roles').select('user_id, role'),
      ]);
      if (assignmentsRes.error) throw assignmentsRes.error;
      const completions = completionsRes.data || [];
      const userRoles = rolesRes.data || [];
      const done = new Set(completions.map((c: any) => `${c.material_id}:${c.user_id}`));

      let count = 0;
      (assignmentsRes.data || []).forEach((a: any) => {
        const targets: string[] = a.assigned_to_user_id
          ? [a.assigned_to_user_id]
          : userRoles.filter((r: any) => r.role === a.assigned_to_role).map((r: any) => r.user_id);
        targets.forEach((uid) => {
          if (!done.has(`${a.material_id}:${uid}`)) count += 1;
        });
      });
      return count;
    },
  });

  const tiles = [
    { label: 'Active clients', value: activeClients.length, icon: Users },
    { label: 'Checked in today', value: checkedInCount, icon: LogIn },
    { label: 'Beds available', value: bedsAvailable, icon: BedDouble },
    { label: 'Occupancy', value: `${occupancy}%`, icon: PercentCircle },
  ];

  const actions = [
    { label: 'Unbedded clients', count: unbeddedCount, icon: BedDouble, section: 'clients' },
    { label: 'Open alerts', count: openAlerts, icon: AlertTriangle, section: 'alerts' },
    { label: 'Overdue trainings', count: overdueTrainings as number, icon: GraduationCap, section: 'training' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <Card key={t.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <t.icon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{t.label}</p>
                <p className="text-xl font-semibold">{t.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Action items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {actions.map((a) => {
              const empty = !a.count;
              return (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => onNavigate?.(a.section)}
                  disabled={!onNavigate}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                    empty ? 'text-muted-foreground' : 'hover:bg-muted/40'
                  } ${onNavigate ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <span className="flex items-center gap-2 text-sm">
                    <a.icon className={`h-4 w-4 ${empty ? 'text-muted-foreground' : ''}`} />
                    {a.label}
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge variant={empty ? 'outline' : 'secondary'}>{a.count}</Badge>
                    {onNavigate && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminHome;
